import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PhoneIcon from "@mui/icons-material/Phone";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import UploadIcon from "@mui/icons-material/Upload";
import SettingsIcon from "@mui/icons-material/Settings";
import { setModal } from "../../features/modal/ModalSlice";
import CustomButton from "../../components/Button/CustomButton";
import apiClient from "../../services/apiClient";
import toast from "react-hot-toast";

const PhoneNumbers = () => {
  const dispatch = useDispatch();
  const modal = useSelector((state) => state.modal?.modal);

  const [phoneNumbers, setPhoneNumbers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  useEffect(() => {
    fetchPhoneNumbers();
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    if (modal?.isUpdatedIn === "PHONE_NUMBERS") {
      fetchPhoneNumbers();
    }
  }, [modal]);

  const fetchPhoneNumbers = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/assignedPhoneDetails", {
        page: pagination.pageIndex + 1, // API expects 1-based page number
        page_size: pagination.pageSize,
      });

      if (response?.status && Array.isArray(response.data)) {
        // Map API data to match existing structure
        const apiPhoneNumbers = response.data.map((item) => ({
          id: item._id,
          number:
            item.data?.effective_caller_id_number ||
            item.data?.username ||
            "N/A",
          countryCode: "US", // Default, can be extracted from data if available
          provider: item.data?.sip_profile_name || "PhoneSystem",
          type: "Extension",
          assignedTo:
            item.assigned_phone_status === "assigned" && item.user_id
              ? `User ${item.data?.username}`
              : null,
          status:
            item.assigned_phone_status === "assigned" ? "Active" : "Available",
          capabilities: ["Voice"],
          monthlyCost: 0,
          // Additional fields from API
          username: item.data?.username,
          regStatus: item.data?.reg_status,
          regIp: item.data?.reg_ip,
          regUserAgent: item.data?.reg_user_agent,
          isAiAgent: item.data?.is_aiagent === 1,
          rawData: item, // Keep raw data for reference
        }));

        setPhoneNumbers(apiPhoneNumbers);

        // Update total row count for pagination
        if (response.pagination) {
          setRowCount(response.pagination.total_count);
        }
      }
    } catch (error) {
      console.error("Error fetching phone numbers:", error);
      toast.error("Failed to load phone numbers");
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNumber = () => {
    dispatch(setModal({ type: "BUY_PHONE_NUMBER" }));
  };

  const handleImportNumber = () => {
    dispatch(setModal({ type: "IMPORT_PHONE_NUMBER" }));
  };

  const handleConfigure = (row) => {
    dispatch(setModal({ type: "CONFIGURE_PHONE_NUMBER", data: row }));
  };

  const handleRelease = (row) => {
    if (window.confirm(`Are you sure you want to release ${row.number}?`)) {
      setPhoneNumbers(phoneNumbers.filter((n) => n.id !== row.id));
    }
  };

  const handleTest = (row) => {
    alert(`Test call to ${row.number}`);
  };

  const getProviderColor = (provider) => {
    const colors = {
      Twilio: "#F22F46",
      Telnyx: "#00C16E",
      Vonage: "#0073E6",
      Bandwidth: "#FF6B35",
    };
    return colors[provider] || "#999";
  };

  const getCountryFlag = (code) => {
    const flags = {
      US: "🇺🇸",
      GB: "🇬🇧",
      CA: "🇨🇦",
      AU: "🇦🇺",
    };
    return flags[code] || "🌐";
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "number",
        header: "Phone Number / Extension",
        size: 180,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <span style={{ fontSize: 20 }}>
              {getCountryFlag(row.original.countryCode)}
            </span>
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {row.original.number}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.original.type}
                {row.original.username && ` • Ext ${row.original.username}`}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        accessorKey: "provider",
        header: "Provider",
        size: 120,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue()}
            size="small"
            sx={{
              backgroundColor: `${getProviderColor(cell.getValue())}20`,
              color: getProviderColor(cell.getValue()),
              fontWeight: 600,
              border: `1px solid ${getProviderColor(cell.getValue())}40`,
            }}
          />
        ),
      },
      {
        accessorKey: "assignedTo",
        header: "Assigned To",
        size: 180,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            color={cell.getValue() ? "text.primary" : "text.secondary"}
          >
            {cell.getValue() || "Unassigned"}
          </Typography>
        ),
      },
      // {
      //   accessorKey: "regStatus",
      //   header: "Reg Status",
      //   size: 100,
      //   Cell: ({ row }) => {
      //     const regStatus = row.original.regStatus;
      //     if (!regStatus) return null;

      //     return (
      //       <Chip
      //         label={regStatus}
      //         size="small"
      //         color={regStatus === "On" ? "success" : "default"}
      //         sx={{ fontWeight: 500 }}
      //       />
      //     );
      //   },
      // },
      {
        accessorKey: "status",
        header: "Status",
        size: 100,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue()}
            size="small"
            color={cell.getValue() === "Active" ? "success" : "default"}
            sx={{ fontWeight: 500 }}
          />
        ),
      },
      {
        accessorKey: "capabilities",
        header: "Capabilities",
        size: 150,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {row.original.capabilities.map((cap) => (
              <Chip
                key={cap}
                label={cap}
                size="small"
                sx={{
                  fontSize: 10,
                  height: 20,
                  backgroundColor: "#E8F5E9",
                  color: "#2E7D32",
                }}
              />
            ))}
            {row.original.isAiAgent && (
              <Chip
                label="AI"
                size="small"
                sx={{
                  fontSize: 10,
                  height: 20,
                  backgroundColor: "#E3F2FD",
                  color: "#1976D2",
                }}
              />
            )}
          </Box>
        ),
      },
      {
        accessorKey: "monthlyCost",
        header: "Monthly Cost",
        size: 120,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={600} color="primary">
            ${cell.getValue().toFixed(2)}
          </Typography>
        ),
      },
    ],
    []
  );

  const totalCost = phoneNumbers.reduce((sum, n) => sum + n.monthlyCost, 0);
  const activeNumbers = phoneNumbers.filter(
    (n) => n.status === "Active"
  ).length;
  const availableNumbers = phoneNumbers.filter((n) => !n.assignedTo).length;
  const displayTotalCount = rowCount > 0 ? rowCount : phoneNumbers.length;

  return (

    <>
    
    {/* Header */}
    <div className="pageTitle1 px-5 py-4">
      <h3>Phone Numbers</h3>
      <p>Manage phone numbers from VOIP providers (Telnyx)</p>
    </div>


    <Box sx={{ padding: 3 }}>
      {/* Stats Cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <Box
          sx={{
            flex: 1,
            p: 3,
            borderRadius: "12px",
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#667eea", mb: 0.5 }}>
            {displayTotalCount}
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280" }}>
            Total Numbers
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            p: 3,
            borderRadius: "12px",
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#10b981", mb: 0.5 }}>
            {activeNumbers}
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280" }}>
            Active Numbers
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            p: 3,
            borderRadius: "12px",
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#3b82f6", mb: 0.5 }}>
            {availableNumbers}
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280" }}>
            Available Numbers
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            p: 3,
            borderRadius: "12px",
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#f59e0b", mb: 0.5 }}>
            ${totalCost.toFixed(2)}
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280" }}>
            Monthly Cost
          </Typography>
        </Box>
      </Box>

      {/* Table */}
      <MaterialReactTable
        columns={columns}
        data={phoneNumbers}
        rowCount={rowCount}
        manualPagination
        onPaginationChange={setPagination}
        state={{
          isLoading: loading,
          pagination: pagination,
        }}
        enableRowActions
        positionActionsColumn="last"
        renderRowActions={({ row }) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Test Call">
              <IconButton
                size="small"
                onClick={() => handleTest(row.original)}
                sx={{ color: "success.main" }}
              >
                <PhoneIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Configure">
              <IconButton
                size="small"
                onClick={() => handleConfigure(row.original)}
                sx={{ color: "primary.main" }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Release">
              <IconButton
                size="small"
                onClick={() => handleRelease(row.original)}
                sx={{ color: "error.main" }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        enableColumnFilters
        enableGlobalFilter
        enableSorting
        muiTablePaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 2,
            border: "1px solid #E0E0E0",
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            backgroundColor: "#F9FAFB",
            fontWeight: 600,
          },
        }}
      />
    </Box>


    </>



  );
};

export default PhoneNumbers;
